/**
 * capability-catalog (US-AIF.P0-0243-S01 · @UC9415) — DANH MỤC NĂNG LỰC AI-WRITE bật/tắt được.
 *
 * VÌ SAO TỒN TẠI (@gate config,ai — chạm QUYỀN AI GHI TIỀN):
 *  - Admin cần bật/tắt từng usecase AI form (P0: chỉ COL-EQUAL "thu đều"). Tắt ⇒ router KHÔNG mở form;
 *    trả `unsupported` + deep-link GUI (người dùng vẫn làm được việc, chỉ không qua chat).
 *  - Catalog sống ở CODE (không đẻ usecase bằng SQL) — MỘT NGUỒN SỰ THẬT cho:
 *      · router gate (turn-router đọc `disabledUsecases` để chặn `buildForm`),
 *      · admin UI (danh mục hiển thị + toggle),
 *      · resolver config (usecaseCode ngoài catalog ⇒ config mồ côi, BỎ QUA — không làm luật đổi ngầm).
 *  - Mirror `AI_CAPABILITIES` (năng lực khai báo cho user) NHƯNG tách bạch: catalog này là ĐƠN VỊ CẤU
 *    HÌNH (usecase form admin gác cổng), không phải câu chữ marketing năng lực.
 *
 * HÀM/HẰNG THUẦN (DP-3): KHÔNG DB, KHÔNG LLM. Thêm usecase form mới ⇒ thêm 1 dòng ở đây (Phase sau).
 *
 * BẤT BIẾN:
 *  - `enabledByDefault = true`: usecase đã ship ⇒ MẶC ĐỊNH BẬT (admin phải chủ động TẮT). Fail-open theo
 *    HƯỚNG NĂNG LỰC (không phải quyền): tắt là quyết định vận hành có chủ đích, ghi audit.
 *  - `deepLink`: cửa GUI thay thế khi usecase tắt (KHÔNG ngõ cụt — DP-7). BẮT BUỘC non-null.
 *  - `intentCode` khớp EXACT intentCode router/form dùng (COL_EQUAL_INTENT_CODE) — lệch ⇒ gate không bắt.
 */

import { COL_EQUAL_INTENT_CODE } from "./col-equal-form.js";
import {
  COL_OPEN_AMOUNT_INTENT_CODE,
  COL_TARGET_SPLIT_INTENT_CODE,
  COL_RECURRING_INTENT_CODE,
  COL_DEADLINE_INTENT_CODE,
} from "./collection-form-specs.js";
import {
  COL_PER_MEMBER_INTENT_CODE,
  COL_TIERED_INTENT_CODE,
} from "./col-per-member-tiered-form.js";
import { EXP_F_NOALLOC_INTENT_CODE } from "./exp-adhoc-form.js";
import { EXP_F_EQUAL_INTENT_CODE } from "./exp-f-equal-form.js";
import {
  EXP_F_RATIO_INTENT_CODE,
  EXP_F_EXCLUDE_INTENT_CODE,
  EXP_P_ADVANCE_EQUAL_INTENT_CODE,
  EXP_P_PAY_FOR_ONE_INTENT_CODE,
} from "./exp-split-variants-form.js";
import {
  VOTE_EXPENSE_INTENT_CODE,
  VOTE_GOVERNANCE_INTENT_CODE,
} from "./vote-open-form.js";
import {
  DEBT_PAY_BEHALF_INTENT_CODE,
  DEBT_REMIND_INTENT_CODE,
  DEBT_CONFIRM_INTENT_CODE,
} from "./debt-forms.js";
import {
  ONB_CREATE_GROUP_INTENT_CODE,
  ONB_ADD_MEMBERS_INTENT_CODE,
  ONB_CREATE_FUND_INTENT_CODE,
} from "./onboarding-forms.js";
import { ROUTE_CATALOG, resolveRoute } from "./orchestration/route-catalog.js";

/** Một usecase AI form admin bật/tắt được. */
export interface AiCapabilityCatalogItem {
  /**
   * Mã usecase = intentCode router/form dùng (khoá override + gate). Ổn định cho client/test/DB.
   * (P0: chỉ COL-EQUAL.)
   */
  readonly usecaseCode: string;
  /** Nhãn cho admin (tiếng Việt). */
  readonly labelVi: string;
  /** Nhãn cho admin (tiếng Anh). */
  readonly labelEn: string;
  /** `write` = usecase GHI/tạo dữ liệu tài chính (bật/tắt chạm quyền AI-write). */
  readonly tier: "write";
  /**
   * Cửa GUI thay thế khi usecase tắt (KHÔNG ngõ cụt).
   *
   * 🔴 US-AI-0310 — là **KEY trỏ `ROUTE_CATALOG`**, KHÔNG phải chuỗi path.
   * Bản cũ để chuỗi thô (`"/collections"`, `"/expenses"`) và **không guard nào canh** ⇒ 16/18 usecase
   * dẫn tới màn KHÔNG TỒN TẠI trong `app_router.dart`. Dùng key thì `deep-link-usage.spec.ts` đối
   * chiếu được với router thật, thêm usecase quên khai ⇒ ĐỎ tự động.
   */
  readonly deepLinkKey: string;
  /** Mặc định bật (usecase đã ship). Admin TẮT là quyết định có chủ đích (audit). */
  readonly enabledByDefault: boolean;
}

/**
 * DANH MỤC (P0: đúng 1 dòng — COL-EQUAL). Phase sau thêm dòng; router gate + admin UI tự nhận vì đọc
 * cùng catalog này (không hardcode danh sách ở hai nơi).
 */
export const AI_CAPABILITY_CATALOG: readonly AiCapabilityCatalogItem[] = Object.freeze([
  Object.freeze({
    usecaseCode: COL_EQUAL_INTENT_CODE,
    labelVi: "Mở đợt thu đều (mỗi người một số) qua trò chuyện",
    labelEn: "Start an equal-split collection via chat",
    tier: "write",
    // Tắt ⇒ user vẫn tạo đợt thu ở màn quản lý đợt thu (GUI) — không mất khả năng, chỉ đổi lối vào.
    deepLinkKey: "COLLECTION_CREATE",
    enabledByDefault: true,
  }),
  // US-AIF.P1-0248 (@UC9420): thu TỰ NGUYỆN (mỗi người đóng tuỳ tâm) — engine open_amount.
  Object.freeze({
    usecaseCode: COL_OPEN_AMOUNT_INTENT_CODE,
    labelVi: "Mở đợt thu tự nguyện (đóng tuỳ tâm) qua trò chuyện",
    labelEn: "Start a voluntary (open-amount) collection via chat",
    tier: "write",
    deepLinkKey: "COLLECTION_CREATE",
    enabledByDefault: true,
  }),
  // US-AIF.P1-0248 (@UC9421): đặt TỔNG mục tiêu, hệ thống chia đầu người — engine target_split.
  Object.freeze({
    usecaseCode: COL_TARGET_SPLIT_INTENT_CODE,
    labelVi: "Mở đợt thu theo mục tiêu (chia đầu người) qua trò chuyện",
    labelEn: "Start a target-total (split per member) collection via chat",
    tier: "write",
    deepLinkKey: "COLLECTION_CREATE",
    enabledByDefault: true,
  }),
  // US-AIF.P1-0250 (@UC9423): mỗi thành viên một số riêng (list entries) — engine per_member.
  Object.freeze({
    usecaseCode: COL_PER_MEMBER_INTENT_CODE,
    labelVi: "Mở đợt thu mỗi người một số riêng qua trò chuyện",
    labelEn: "Start a per-member (individual amounts) collection via chat",
    tier: "write",
    deepLinkKey: "COLLECTION_CREATE",
    enabledByDefault: true,
  }),
  // US-AIF.P1-0250 (@UC9424): thu theo bậc (tier defs + map member→bậc) — engine tiered.
  Object.freeze({
    usecaseCode: COL_TIERED_INTENT_CODE,
    labelVi: "Mở đợt thu theo bậc qua trò chuyện",
    labelEn: "Start a tiered collection via chat",
    tier: "write",
    deepLinkKey: "COLLECTION_CREATE",
    enabledByDefault: true,
  }),
  // US-AIF.P1-0251 (@UC9425): thu ĐỊNH KỲ tự lặp (createSchedule tạo lịch + sinh đợt đầu) — engine schedule.
  Object.freeze({
    usecaseCode: COL_RECURRING_INTENT_CODE,
    labelVi: "Mở đợt thu định kỳ (tự lặp) qua trò chuyện",
    labelEn: "Start a recurring collection via chat",
    tier: "write",
    deepLinkKey: "COLLECTION_CREATE",
    enabledByDefault: true,
  }),
  // US-AIF.P1-0251 (@UC9426): thu có HẠN CHÓT (quá hạn → nợ) — engine adhoc (createAdhoc + dueDate).
  Object.freeze({
    usecaseCode: COL_DEADLINE_INTENT_CODE,
    labelVi: "Mở đợt thu có hạn chót (quá hạn ghi nợ) qua trò chuyện",
    labelEn: "Start a deadline collection (overdue becomes debt) via chat",
    tier: "write",
    deepLinkKey: "COLLECTION_CREATE",
    enabledByDefault: true,
  }),
  // 🔴 US-AIF.P2-0253 (@UC9432): CHI chung KHÔNG phân bổ (quỹ trả 100%, memberId=null) — engine expense
  // (ExpenseService.create split fund_flow rỗng). Usecase CHI đầu tiên vào catalog (EXP-ADHOC là foundation
  // ẨN). Tắt ⇒ user vẫn tạo khoản chi ở màn quản lý chi (GUI /expenses) — không mất khả năng, chỉ đổi lối vào.
  Object.freeze({
    usecaseCode: EXP_F_NOALLOC_INTENT_CODE,
    labelVi: "Ghi khoản chi chung (quỹ trả, không phân bổ) qua trò chuyện",
    labelEn: "Record a shared expense (fund-paid, no allocation) via chat",
    tier: "write",
    deepLinkKey: "EXPENSE_CREATE",
    enabledByDefault: true,
  }),
  // 🔴 US-AIF.P2-0253c (@UC9433): CHI TỪ QUỸ, CHIA ĐỀU GÁNH NẶNG cho tất cả (quỹ trả 100% CASH +
  // participant Σshare = amount, ZERO nợ) — engine expense source `fund_flow_allocated` + rule `equal`.
  // Tắt ⇒ user vẫn tạo khoản chi chia đều ở màn quản lý chi (GUI /expenses) — đổi lối vào, không mất khả năng.
  Object.freeze({
    usecaseCode: EXP_F_EQUAL_INTENT_CODE,
    labelVi: "Ghi khoản chi từ quỹ, chia đều gánh nặng cho tất cả qua trò chuyện",
    labelEn: "Record a fund-paid expense with burden split equally, via chat",
    tier: "write",
    deepLinkKey: "EXPENSE_CREATE",
    enabledByDefault: true,
  }),
  // 🔴 US-AIF.P2-0254 (@UC9433): CHI TỪ QUỸ, chia gánh nặng theo TỶ LỆ % (real_scenarios.md:59 — 600 điện:
  // A 40%, B/C/D 20%) — engine expense source `fund_flow_allocated` + rule `ratio` (weight = %).
  Object.freeze({
    usecaseCode: EXP_F_RATIO_INTENT_CODE,
    labelVi: "Ghi khoản chi từ quỹ, chia gánh nặng theo tỷ lệ % qua trò chuyện",
    labelEn: "Record a fund-paid expense with burden split by percentage, via chat",
    tier: "write",
    deepLinkKey: "EXPENSE_CREATE",
    enabledByDefault: true,
  }),
  // 🔴 US-AIF.P2-0254 (@UC9434): CHI TỪ QUỸ, chia đều NHƯNG LOẠI TRỪ một số người (real_scenarios.md:62 —
  // 300 tiệc, D không dự → A,B,C mỗi người 100) — source `fund_flow_allocated` + rule `equal` trên tập còn lại.
  Object.freeze({
    usecaseCode: EXP_F_EXCLUDE_INTENT_CODE,
    labelVi: "Ghi khoản chi từ quỹ, chia đều loại trừ một số người qua trò chuyện",
    labelEn: "Record a fund-paid expense split equally excluding some members, via chat",
    tier: "write",
    deepLinkKey: "EXPENSE_CREATE",
    enabledByDefault: true,
  }),
  // 🔴 US-AIF.P2-0254 (@UC9435): THÀNH VIÊN ỨNG TIỀN TÚI, cả nhóm chia đều rồi HOÀN người ứng
  // (real_scenarios.md:72 — A trả 400 tiền ăn; mỗi người nợ A 100) — source `peer` + payerMemberId KHAI
  // (chủ nợ DebtEntry). Quỹ KHÔNG chi. Tắt ⇒ user vẫn ghi ở màn chi (GUI) — đổi lối vào, không mất khả năng.
  Object.freeze({
    usecaseCode: EXP_P_ADVANCE_EQUAL_INTENT_CODE,
    labelVi: "Ghi khoản một người ứng trước, cả nhóm chia đều và hoàn lại qua trò chuyện",
    labelEn: "Record a member-advanced expense split equally and repaid to the payer, via chat",
    tier: "write",
    deepLinkKey: "EXPENSE_CREATE",
    enabledByDefault: true,
  }),
  // 🔴 US-AIF.P2-0254 (@UC9436): A TRẢ HỘ ĐÚNG MỘT NGƯỜI B; B nợ A toàn bộ (real_scenarios.md:77 — A trả
  // 200 tiền thuốc cho B) — source `peer` + participants = [B] DUY NHẤT + payerMemberId = A.
  Object.freeze({
    usecaseCode: EXP_P_PAY_FOR_ONE_INTENT_CODE,
    labelVi: "Ghi khoản trả hộ một người (người đó nợ lại) qua trò chuyện",
    labelEn: "Record paying on behalf of exactly one member (they owe the payer), via chat",
    tier: "write",
    deepLinkKey: "EXPENSE_CREATE",
    enabledByDefault: true,
  }),
  // 🔴 US-AIF.P3-0255 (@UC9440 · @gate ai): MỞ PHIÊN BIỂU QUYẾT cho một KHOẢN CHI chờ quyết định —
  // engine `ExpenseDecisionService.openVote` (luật quorum/ngưỡng/hạn SNAPSHOT từ config quỹ, KHÔNG do
  // form nhập — DP-3). Mở phiên KHÔNG post ledger (tiền chỉ rời quỹ khi phiên PASSED).
  // Tắt ⇒ user vẫn mở phiên ở màn quản lý chi (GUI /expenses) — đổi lối vào, không mất khả năng.
  Object.freeze({
    usecaseCode: VOTE_EXPENSE_INTENT_CODE,
    labelVi: "Mở phiên biểu quyết cho khoản chi qua trò chuyện",
    labelEn: "Open a vote session for an expense, via chat",
    tier: "write",
    deepLinkKey: "EXPENSE_CREATE",
    enabledByDefault: true,
  }),
  // 🔴 US-AIF.P3-0255 (@UC9440 · @gate ai): MỞ PHIÊN QUYẾT ĐỊNH QUẢN TRỊ (đổi quy chế / đổi cấu hình /
  // chuyển chủ nhóm) — engine `GovernanceVoteService.createDecision`. 🔴 PHI-TIỀN: đường này KHÔNG BAO
  // GIỜ chạm ledger (governance-vote.service.ts bất biến cốt lõi). Luật snapshot từ config NHÓM (DP-3).
  Object.freeze({
    usecaseCode: VOTE_GOVERNANCE_INTENT_CODE,
    labelVi: "Mở phiên biểu quyết quản trị (quy chế / cấu hình / chủ nhóm) qua trò chuyện",
    labelEn: "Open a governance vote (rules / config / owner transfer), via chat",
    tier: "write",
    deepLinkKey: "FUND_LIST",
    enabledByDefault: true,
  }),
  // 🔴 US-AIF.P3-0256 (@UC9442): TRẢ HỘ khoản thu của người khác (propose → người đó respond mới ghi sổ
  // — 2 phía). Engine `PayOnBehalfService.propose` (POST /v1/member-dues/:id/pay-on-behalf `:56`).
  // Tắt ⇒ user vẫn trả hộ ở màn đợt thu (GUI /collections) — đổi lối vào, không mất khả năng.
  Object.freeze({
    usecaseCode: DEBT_PAY_BEHALF_INTENT_CODE,
    labelVi: "Đề xuất trả hộ khoản thu cho người khác qua trò chuyện",
    labelEn: "Propose paying a member's due on their behalf, via chat",
    tier: "write",
    deepLinkKey: "COLLECTION_CREATE",
    enabledByDefault: true,
  }),
  // 🔴 US-AIF.P3-0256 (@UC9443): NHẮC người chưa đóng một đợt thu — `SendRemindersService.send`
  // (POST /v1/collections/:id/reminders `:37`). KHÔNG ledger (outbox + notification in_app).
  Object.freeze({
    usecaseCode: DEBT_REMIND_INTENT_CODE,
    labelVi: "Nhắc những người chưa đóng của một đợt thu qua trò chuyện",
    labelEn: "Remind members who have not paid a collection, via chat",
    tier: "write",
    deepLinkKey: "COLLECTION_REMIND",
    enabledByDefault: true,
  }),
  // 🔴 US-AIF.P3-0256 (@UC9444): CHỦ NỢ xác nhận (accept/decline) một dàn xếp nợ đang chờ —
  // `PeerDebtSettleService.respond` (POST /v1/funds/:id/debts/settlements/:settlementId/respond `:69`).
  // 2 PHÍA: con nợ CLAIM, chủ nợ mới đóng được (một phía KHÔNG tự quyết). accept ⇒ post ledger SETTLEMENT.
  Object.freeze({
    usecaseCode: DEBT_CONFIRM_INTENT_CODE,
    labelVi: "Xác nhận đã nhận đủ tiền người khác trả nợ mình, qua trò chuyện",
    labelEn: "Confirm receipt of a peer debt settlement (creditor), via chat",
    tier: "write",
    deepLinkKey: "FUND_LIST",
    enabledByDefault: true,
  }),
  // ── 🔴 US-E23.F7-0337 (@gate ai) — KHỞI TẠO (onboarding). NỚI BẤT BIẾN §4 có PO duyệt 29/07. ──────
  //
  // Ba usecase dưới đây là nhóm ĐẦU TIÊN trong catalog KHÔNG phải "thao tác tiền bên trong một quỹ đã
  // tồn tại". Chúng dựng CHỖ CHỨA (nhóm/thành viên/quỹ) — bản thân việc dựng chỗ chứa **0 bút toán**.
  //
  // Chốt an toàn giữ NGUYÊN VẸN (xem docblock `onboarding-forms.ts`): form chỉ mô tả field; confirm gọi
  // ĐÚNG service GUI dùng; mỗi bước một confirm riêng; gate kiểm ở CẢ `start()` LẪN `confirm()`.
  // Tắt ⇒ user vẫn tạo nhóm/thêm người/tạo quỹ ở GUI — đổi lối vào, không mất khả năng.
  Object.freeze({
    usecaseCode: ONB_CREATE_GROUP_INTENT_CODE,
    labelVi: "Tạo nhóm mới qua trò chuyện",
    labelEn: "Create a new group via chat",
    tier: "write",
    // 🔴 Bộ khung mobile MỚI (PR #324) chưa dựng lại `/create-group` ⇒ trỏ màn CÓ THẬT.
    deepLinkKey: "APP_HOME",
    enabledByDefault: true,
  }),
  Object.freeze({
    usecaseCode: ONB_ADD_MEMBERS_INTENT_CODE,
    labelVi: "Thêm thành viên vào nhóm qua trò chuyện",
    labelEn: "Add members to a group via chat",
    tier: "write",
    deepLinkKey: "APP_HOME",
    enabledByDefault: true,
  }),
  Object.freeze({
    usecaseCode: ONB_CREATE_FUND_INTENT_CODE,
    labelVi: "Tạo quỹ cho nhóm qua trò chuyện",
    labelEn: "Create a fund for a group via chat",
    tier: "write",
    deepLinkKey: "APP_HOME",
    enabledByDefault: true,
  }),
] as AiCapabilityCatalogItem[]);

/** Tra usecase trong catalog (undefined nếu mã ngoài catalog — config mồ côi ⇒ caller BỎ QUA). */
export function findCapabilityByCode(usecaseCode: string): AiCapabilityCatalogItem | undefined {
  return AI_CAPABILITY_CATALOG.find((c) => c.usecaseCode === usecaseCode);
}

/**
 * deep-link GUI của usecase — trả PATH đã giải, dùng cho CTA khi usecase bị admin tắt.
 *
 * 🔴 US-AI-0310 — giải từ KEY qua `ROUTE_CATALOG` thay vì trả chuỗi thô. Hệ quả tốt: path trả ra
 * LUÔN là route có thật trong `app_router.dart` (guard đối chiếu). Bản cũ trả `/collections` và
 * `/expenses` — **cả hai KHÔNG TỒN TẠI** ⇒ user bấm "cửa vào GUI" là vào màn lỗi.
 *
 * Thiếu tham số (vd chưa chốt quỹ nên không dựng được `/funds/:fundId/create-collection`) hoặc mã
 * ngoài catalog ⇒ rơi về `FUND_LIST` — route KHÔNG tham số, luôn giải được, luôn tồn tại.
 * Vẫn giữ đúng bất biến "không ngõ cụt", chỉ khác là cửa vào nay THẬT SỰ mở được.
 */
export function capabilityDeepLink(
  usecaseCode: string,
  params: Readonly<Record<string, string | null | undefined>> = {},
): string {
  const key = findCapabilityByCode(usecaseCode)?.deepLinkKey;
  const resolved = key === undefined ? null : resolveRoute(key, params);
  return resolved?.path ?? ROUTE_CATALOG.mobile.FUND_LIST!.pathTemplate;
}
